import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { 
  Card, 
  CardContent, 
  CardMedia, 
  Typography, 
  Button, 
  Box, 
  IconButton,
  Snackbar,
  Alert
} from '@mui/material';
import { styled } from '@mui/material/styles';
import FavoriteBorderIcon from '@mui/icons-material/FavoriteBorder';
import FavoriteIcon from '@mui/icons-material/Favorite';
import AddShoppingCartIcon from '@mui/icons-material/AddShoppingCart';
import { useNavigate } from 'react-router-dom';
import { useCart } from '../../contexts/CartContext';
import { useAuth } from '../../contexts/AuthContext';

const StyledCard = styled(Card)(({ theme }) => ({
  height: '100%',
  display: 'flex',
  flexDirection: 'column',
  transition: 'transform 0.3s ease, box-shadow 0.3s ease',
  '&:hover': {
    transform: 'translateY(-5px)',
    boxShadow: '0 8px 16px rgba(0, 0, 0, 0.1)',
  },
  position: 'relative',
}));

const StyledCardMedia = styled(CardMedia)(({ theme }) => ({
  paddingTop: '100%', // 1:1 Aspect Ratio
  backgroundSize: 'cover',
  transition: 'transform 1s ease',
  '&:hover': {
    transform: 'scale(1.05)',
  },
}));

const MediaWrapper = styled(Box)({
  overflow: 'hidden',
  position: 'relative',
});

const FavoriteButton = styled(IconButton)(({ theme }) => ({
  position: 'absolute',
  top: 8,
  right: 8,
  backgroundColor: 'rgba(255, 255, 255, 0.8)',
  '&:hover': {
    backgroundColor: 'rgba(255, 255, 255, 0.9)',
  },
  zIndex: 1,
}));

const ProductCard = ({ product }) => {
  const [isFavorite, setIsFavorite] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const navigate = useNavigate();
  const { addItem } = useCart();
  const { currentUser } = useAuth();

  const toggleFavorite = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsFavorite(!isFavorite);
  };

  const addToCart = (e) => {
    e.preventDefault();
    e.stopPropagation();
    
    // 使用CartContext的addItem函数添加商品到购物车
    addItem(product, 1);
    
    // 触发Google Tag Manager的添加到购物车事件
    if (window.dataLayer) {
      window.dataLayer.push({
        'event': 'addToCart',
        'ecommerce': {
          'currencyCode': 'SGD',
          'add': {
            'products': [{
              'name': product.name,
              'id': product.id,
              'price': product.price,
              'quantity': 1
            }]
          }
        }
      });
      console.log('GTM事件：添加到购物车', product.name);
    }
    
    // 检查用户是否已登录
    if (!currentUser) {
      // 显示提示消息
      setSnackbar({
        open: true,
        message: '已加入购物车，请登录后查看购物车',
        severity: 'info'
      });
      return;
    }
    
    // 用户已登录，显示成功消息
    setSnackbar({
      open: true,
      message: '已成功加入购物车',
      severity: 'success'
    });
  };

  const handleCardClick = () => {
    navigate(`/product/${product.id}`);
  };

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  return (
    <>
      <StyledCard onClick={handleCardClick}>
        <FavoriteButton onClick={toggleFavorite}>
          {isFavorite ? (
            <FavoriteIcon color="error" />
          ) : (
            <FavoriteBorderIcon color="action" />
          )}
        </FavoriteButton>
        
        <MediaWrapper>
          <StyledCardMedia
            image={product.image || (product.images && product.images.length > 0 ? product.images[0] : "https://storage.googleapis.com/destinycrystal-ff557.firebasestorage.app/placeholders/no-image.png")}
            title={product.name}
          />
        </MediaWrapper>
        
        <CardContent sx={{ flexGrow: 1 }}>
          <Typography gutterBottom variant="h6" component="div" noWrap>
            {product.name}
          </Typography>
          
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2, height: '3em', overflow: 'hidden' }}>
            {product.description?.substring(0, 80)}
            {product.description?.length > 80 ? '...' : ''}
          </Typography>
          
          <Box display="flex" justifyContent="space-between" alignItems="center">
            <Typography variant="h6" color="primary">
              S${product.price}
              {product.discountPrice && (
                <Typography 
                  component="span" 
                  sx={{ 
                    textDecoration: 'line-through', 
                    color: 'text.secondary',
                    fontSize: '0.8rem', 
                    ml: 1 
                  }}
                >
                  S${product.discountPrice}
                </Typography>
              )}
            </Typography>
            
            <Button 
              variant="contained" 
              color="primary" 
              onClick={addToCart}
              startIcon={<AddShoppingCartIcon />}
              size="small"
            >
              加入
            </Button>
          </Box>
        </CardContent>
      </StyledCard>

      {/* 添加到购物车成功提示 */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert 
          onClose={handleCloseSnackbar} 
          severity={snackbar.severity} 
          variant="filled"
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </>
  );
};

export default ProductCard;